import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useMaintenance } from "../context/MaintenanceContext";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const { refresh: refreshMaint } = useMaintenance();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) {
      toast.success(`Welcome back, ${res.user.name}`);
      const dest = loc.state?.from || (res.user.role === "admin" ? "/admin" : res.user.role === "creator" ? "/creator" : "/browse");
      nav(dest);
    } else if (res.maintenance) {
      // Backend rejected a non-admin login because maintenance mode is ON.
      // Refresh the shared maintenance context so <MaintenanceGate> renders
      // the Scheduled Maintenance page immediately on the next route.
      await refreshMaint();
      toast.message("Scheduled maintenance in progress.");
      nav("/");
    } else {
      setError(res.error);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] grid grid-cols-1 lg:grid-cols-2" data-testid="login-page">
      <div className="hidden lg:flex relative overflow-hidden items-end p-12">
        <img src="https://images.pexels.com/photos/12013504/pexels-photo-12013504.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=900" alt="" className="absolute inset-0 h-full w-full object-cover opacity-60" />
        <div className="absolute inset-0 bg-gradient-to-tr from-[#0A0A0A] via-transparent to-transparent" />
        <div className="relative">
          <p className="text-eyebrow">Christoreel</p>
          <h2 className="font-display text-5xl font-black mt-3 leading-tight">Welcome back to <br /> the theater of light.</h2>
        </div>
      </div>
      <div className="flex items-center justify-center p-8">
        <form onSubmit={submit} className="w-full max-w-md" data-testid="login-form">
          <Link to="/" className="inline-flex items-center gap-2 mb-10">
            <span className="inline-flex h-9 w-9 items-center justify-center rounded-full overflow-hidden bg-[#FDF0CA]"><img src="/logo192.png" alt="Christoreel" className="h-9 w-9 object-cover" /></span>
            <span className="font-display text-2xl font-black">Christoreel</span>
          </Link>
          <h1 className="font-display text-4xl font-black">Sign in</h1>
          <p className="text-white/50 mt-2 text-sm">Continue to your library.</p>

          <div className="mt-8 space-y-4">
            <div>
              <label className="text-eyebrow mb-2 block">Email</label>
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="input-dark" data-testid="login-email-input" placeholder="you@example.com" />
            </div>
            <div>
              <label className="text-eyebrow mb-2 block">Password</label>
              <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="input-dark" data-testid="login-password-input" placeholder="••••••••" />
            </div>
            {error && <p className="text-sm text-red-400" data-testid="login-error">{error}</p>}
            <button type="submit" disabled={loading} className="ember-btn w-full justify-center" data-testid="login-submit-button">
              {loading ? "Signing in..." : "Sign in"}
            </button>
          </div>

          <p className="mt-8 text-sm text-white/50 text-center">
            New to Christoreel? <Link to="/register" className="text-[#E65F2B] hover:text-[#F97316] font-semibold" data-testid="login-register-link">Create an account</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
